import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import { Input, InputProps } from 'UI';
import { ONCHANGE_DEBOUNCE_INTERVAL } from 'utils/hooks/useInput';

export interface AutocompleteSearchInputProps extends InputProps {
  name?: string;
  readOnly?: boolean;
  dark?: boolean;
}

const Wrapper = styled.div<{ $dark?: boolean }>`
  max-height: 31px;
  min-height: 31px;
  display: flex;
  align-items: center;
  flex: 1;
  border-radius: 3px;
  text-align: left;
  padding: 0 5px;
  background-color: ${({ theme, $dark }) => ($dark ? theme.color.grey30 : theme.color.white)};
  color: ${({ theme, $dark }) => ($dark ? theme.color.grey : theme.color.brand_black)};
`;

const SForm = styled.form`
  display: flex;
  flex: 1;
`;

export function SearchAutocompleteInput({
  id,
  name,
  value,
  onChange,
  onChangeDebounced,
  onFocus,
  placeholder,
  color,
  onPressEnter,
  passRef,
  onKeyPress,
  onBlur,
  className,
  autoComplete,
  readOnly,
  dark = false,
  'data-cy': dataCy,
}: Readonly<AutocompleteSearchInputProps>) {
  const handleChangeDebounced = useDebouncedCallback((debouncedVal: string, e?: ChangeEvent<HTMLInputElement>) => {
    onChangeDebounced?.(debouncedVal, e);
  }, ONCHANGE_DEBOUNCE_INTERVAL);

  function onChangeLocal(val: string, e?: ChangeEvent<HTMLInputElement>) {
    if (!readOnly) {
      onChange?.(val, e);
      handleChangeDebounced(val.trim(), e);
    }
  }

  function onBlurLocal(val: string, e?: React.FocusEvent<HTMLInputElement>) {
    handleChangeDebounced.cancel();
    onBlur?.(val, e);
  }

  return (
    <SForm onSubmit={(e) => e.preventDefault()}>
      <Wrapper className={className} $dark={dark}>
        <Input
          id={id}
          name={name}
          autoComplete={autoComplete}
          value={value}
          onChange={onChangeLocal}
          onFocus={onFocus}
          placeholder={placeholder}
          color={color}
          onPressEnter={onPressEnter}
          passRef={passRef}
          onKeyPress={onKeyPress}
          onBlur={onBlurLocal}
          ellipsis
          data-cy={dataCy ?? 'search-bar'}
          readOnly={readOnly}
        />
      </Wrapper>
    </SForm>
  );
}
